import { useState } from "react";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import MoreVertOutlinedIcon from "@mui/icons-material/MoreVertOutlined";
import { Box, IconButton, Menu, MenuItem } from "@mui/material";
import Chip from "@mui/material/Chip";
import Radio from "@mui/material/Radio";
import type { Theme } from "@mui/material/styles";
import type { GridColDef, GridRenderCellParams, GridValidRowModel } from "@mui/x-data-grid";
import Badge from "@components/Badge/Badge";
import type { NotificationStatus } from "@components/NotificationsDropdown/NotificationsDropdown.types.ts";
import { getDepartureStatusBadgeType } from "@features/vessel-call/grid/utils";
import { format } from "date-fns";
import { useTranslation } from "react-i18next";
import type { Header } from "./CustomDataTable.types";

function ActionCell<T>({
  opts,
  row,
}: {
  opts?: { onEdit?: (row: T) => void; onDelete?: (row: T) => void };
  row: T;
}) {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);
  return (
    <>
      <IconButton
        size="small"
        onClick={(e) => {
          e.stopPropagation();
          setAnchorEl(e.currentTarget);
        }}
      >
        <MoreVertOutlinedIcon fontSize="small" />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        onClick={(e) => e.stopPropagation()}
      >
        <MenuItem
          onClick={() => {
            opts?.onEdit?.(row);
            setAnchorEl(null);
          }}
        >
          {t("common.edit") ?? "Edit"}
        </MenuItem>
        <MenuItem
          onClick={() => {
            opts?.onDelete?.(row);
            setAnchorEl(null);
          }}
        >
          {t("common.delete") ?? "Delete"}
        </MenuItem>
      </Menu>
    </>
  );
}

export const getBadgeType = (status?: string): "success" | "warning" | "error" | "neutral" => {
  if (
    status === "Rejected" ||
    status === "Expired" ||
    status === "Interrupted - in Port" ||
    status === "Interrupted - leave Port" ||
    status === "Cancelled"
  )
    return "error";
  if (
    status === "Approved" ||
    status === "Completed" ||
    status === "Verified" ||
    status === "Closed" ||
    status === "Departure Approved" ||
    status === "Departed" ||
    status === "Reinstated"
  )
    return "success";
  if (status === "Arrival Draft" || status === "Pre-arrival Draft" || status === "Draft")
    return "neutral";
  return "warning";
};

export const getStatusChipSx = (theme: Theme, status: NotificationStatus | string = "Pending") => {
  const bgByStatus: Record<NotificationStatus, string> = {
    "Pending": theme.palette.warning.main,
    "Suspend": theme.palette.custom.main,
    "Unsuspend": theme.palette.custom.secondary,
    "Expired": theme.palette.error.main,
    "Document about to expire": theme.palette.warning.main,
    "Expiring soon": theme.palette.warning.main,
    "Expires today": theme.palette.warning.main,
    "Cancelled": theme.palette.error.main,
    "Approved": theme.palette.success.main,
    "Completed": theme.palette.success.main,
    "Resubmit": theme.palette.error.main,
    "Delegated": theme.palette.info.main,
    "Permanently rejected": theme.palette.error.main,
    "Restricted": theme.palette.custom.main,
    "Clear to operate": theme.palette.custom.secondary,
    "Updated": theme.palette.info.main,
    "Closed": theme.palette.success.main,
    "Vessel Call Closed": theme.palette.success.main,
    "Overdue": theme.palette.error.main,
    "Dispute": theme.palette.error.main,
    "Verified": theme.palette.warning.main,
    "Rejected by Marine Department": theme.palette.error.main,
    "Rejected by Customs": theme.palette.error.main,
    "Customs Payment Required": theme.palette.warning.main,
    "Autogenerated": theme.palette.success.main,
    "Pending Approval": theme.palette.warning.main,
    "Pending Pre-approval": theme.palette.warning.main,
    "Submitted": theme.palette.info.main,
    "Interrupted In-Port": theme.palette.error.main,
    "Interrupted Leave-Port": theme.palette.error.main,
    "Rejected": theme.palette.error.main,
    "Payment Required": theme.palette.error.main,
    "Reinstated": theme.palette.success.main,
    "Awaiting Approval": theme.palette.warning.main,
    "Pending Resolution": theme.palette.warning.main,
    "Resolved": theme.palette.success.main,
    // DFT-0044 (FE-71/72): matches the wireframe's own chip colors
    // (22_Notifications_dropdown.png) - orange/warning, green/success, red/error respectively.
    "Bay Hire License Renewal": theme.palette.warning.main,
    "Bay Hire License Approved": theme.palette.success.main,
    "Bay Hire License Expired": theme.palette.error.main,
    // DFT-0019: no wireframe covers this chip. Requester decision (2026-08-31): same red as
    // "Bay Hire License Expired", matching NotificationsDropdown.styles.ts's duplicate map.
    "Bay Hire License Expiring": theme.palette.error.main,
    "Update Approved": theme.palette.success.main,
    "Update Rejected": theme.palette.error.main,
  } as const;

  const bg = (bgByStatus as Record<string, string>)[status] ?? theme.palette.warning.main;

  return {
    "bgcolor": bg,
    "color": theme.palette.text.white,
    "height": 24,
    "borderRadius": 9999,
    "px": 0,
    "& .MuiChip-label": {
      paddingLeft: "0.5rem",
      paddingRight: "0.5rem",
      fontSize: 12,
      lineHeight: "20px",
      fontWeight: theme.typography.fontWeightMedium,
    },
  } as const;
};

const sizingFromHeader = (h: Header, fallbackFlex = 1, fallbackMinW = 130) => {
  if (typeof h.percent === "number") {
    return { flex: h.percent, minWidth: fallbackMinW } as const;
  }
  const flex = typeof h.flex === "number" ? h.flex : fallbackFlex;
  const minWidth = typeof h.minWidth === "number" ? h.minWidth : fallbackMinW;
  return { flex, minWidth } as const;
};

export function renderColumns<T extends GridValidRowModel>(
  headers: Header[],
  rows: T[],
  theme: Theme,
  shouldFreezeExtraColumn?: boolean,
  opts?: {
    onEdit?: (row: T) => void;
    onDelete?: (row: T) => void;
  },
): GridColDef<T>[] {
  return headers.map((h, idx): GridColDef<T> => {
    if (h.field === "action") {
      const width = h.width ?? Math.max(120, h.minWidth ?? 120);
      return {
        field: h.field,
        headerName: h.columnName,
        width,
        minWidth: width,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        align: "center",
        headerAlign: "center",
        cellClassName: `freeze-right`,
        headerClassName: `freeze-right`,
        renderCell: (p: GridRenderCellParams<T>) => <ActionCell opts={opts} row={p.row} />,
      } as GridColDef<T>;
    }

    if (h.field === "__select") {
      return {
        field: h.field,
        headerName: h.columnName ?? "",
        width: h.width ?? 44,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        align: "center",
        headerAlign: "center",
        renderCell: (p) => (
          <Radio
            size="small"
            checked={Boolean(p.row.__isSelected)}
            onChange={(e) => {
              e.stopPropagation();
              p.row.__onSelect?.();
            }}
            onClick={(e) => {
              e.stopPropagation();
              p.row.__onSelect?.();
            }}
            aria-label={p.row.id ? `select-row-${p.row.id}` : "select-row"}
          />
        ),
        cellClassName: "radio-only-cell",
        headerClassName: "radio-only-cell",
      } as GridColDef<T>;
    }

    if (h.badgeType) {
      const sizing = sizingFromHeader(h, 1, 130);
      const badgeTypeOf = h.badgeType;
      return {
        field: h.field,
        headerName: h.columnName,
        ...sizing,
        renderCell: (p: GridRenderCellParams<T>) => (
          <Box sx={{ fontWeight: theme.typography.fontWeightMedium }}>
            <Badge
              type={badgeTypeOf(p.row)}
              label={String(p.value ?? "-")}
              testId={`badge-${h.field}-${(p.row as { id?: string }).id}`}
            />
          </Box>
        ),
      };
    }

    if (h.renderCell) {
      const sizing = sizingFromHeader(h, 1, 130);
      const renderHeaderCell = h.renderCell;
      return {
        field: h.field,
        headerName: h.columnName,
        ...sizing,
        renderCell: (p: GridRenderCellParams<T>) => <>{renderHeaderCell(p.row)}</>,
      };
    }

    if (h.type === "date") {
      const fmt = (h as any).dateFormat || "dd/MM/yyyy";
      const sizing = sizingFromHeader(h, 1, 130);
      return {
        field: h.field,
        headerName: h.columnName,
        ...sizing,
        renderCell: (params: GridRenderCellParams<T>) => {
          const rawValue = params.row[h.field as keyof T];
          const date = rawValue ? new Date(rawValue as string | number | Date) : null;
          const formatted = date && !isNaN(date.getTime()) ? format(date, fmt) : "N/A";
          return (
            <Box
              sx={{
                fontWeight: theme.typography.fontWeightMedium,
                color: theme.palette.text.primary,
              }}
            >
              {formatted}
            </Box>
          );
        },
      };
    }

    if (h.type === "dateTime") {
      const sizing = sizingFromHeader(h, 1, 130);

      return {
        field: h.field,
        headerName: h.columnName,
        ...sizing,
        renderCell: (params: GridRenderCellParams<T>) => {
          const rawValue = params.row[h.field as keyof T];
          const date = rawValue ? new Date(rawValue as string | number | Date) : null;
          const formatted =
            date && !isNaN(date.getTime()) ? format(date, "MM/dd/yyyy'\n'HH:mm") : "-";
          return (
            <Box
              sx={{
                fontWeight: theme.typography.fontWeightMedium,
                color: theme.palette.text.primary,
              }}
            >
              <div style={{ whiteSpace: "pre-line", lineHeight: "normal" }}>{formatted}</div>
            </Box>
          );
        },
      };
    }

    if (h.field === "arabicName") {
      const sizing = sizingFromHeader(h, 1, 130);
      return {
        field: h.field,
        headerName: h.columnName,
        headerAlign: "left",
        align: "left",
        ...sizing,
        renderCell: (p: GridRenderCellParams<T>) => (
          <Box
            sx={{
              fontWeight: theme.typography.fontWeightMedium,
              color: theme.palette.text.primary,
            }}
          >
            {p.value}
          </Box>
        ),
      };
    }

    // DFT-0039: renderAsChip was only ever wired up for the literal "status"
    // field below - any other column setting renderAsChip: true (e.g. Bay
    // Hire Operations queue's "taskStatus") silently fell through to the
    // generic text-only fallback at the bottom of this function, so the
    // documented Header.renderAsChip contract didn't hold for those
    // columns. Handle it generically here (without the "status" branch's
    // frozen-column positioning, which is specific to that column's usual
    // placement next to row actions).
    if (h.field !== "status" && Boolean(h.renderAsChip)) {
      const sizing = sizingFromHeader(h, 1, 130);
      return {
        field: h.field,
        headerName: h.columnName,
        ...sizing,
        renderCell: (p: GridRenderCellParams<T>) => (
          <Chip size="small" label={(p.value as string) ?? "-"} variant="outlined" />
        ),
      };
    }

    if (h.field === "status" && (h.type as string) !== "logsStatus") {
      const renderAsChip = Boolean((h as any).renderAsChip);
      const percent = (h as any).percent as number | undefined;
      const longest = Math.max(
        (h.columnName ?? "Status").length,
        ...rows.map((r: any) => String(r.statusLabel ?? r.status ?? "").length),
      );
      const defaultSizing = { flex: 0, minWidth: 160, width: longest * 9 };
      const percentSizing =
        typeof percent === "number" ? { flex: percent, minWidth: 140 } : defaultSizing;

      return {
        field: h.field,
        headerName: h.columnName,
        ...percentSizing,
        renderCell: (p: GridRenderCellParams<T>) => {
          const label =
            ((p.row as Record<string, unknown>).statusLabel as string | undefined) ??
            (p.value as string) ??
            "";
          return renderAsChip ? (
            <Chip size="small" label={label} sx={getStatusChipSx(theme, p.value as string)} />
          ) : (
            <Box sx={{ fontWeight: theme.typography.fontWeightMedium }}>
              <Badge
                type={p.row.badgeType || getBadgeType(p.value as string | undefined)}
                label={label}
                testId={`badge-${(p.row as any).id}`}
              />
            </Box>
          );
        },
        cellClassName: `freeze-right ${shouldFreezeExtraColumn ? "freeze-first" : ""}`,
        headerClassName: `freeze-right ${shouldFreezeExtraColumn ? "freeze-first" : ""}`,
      };
    }

    if (h.field === "disputeStatus") {
      const longest = Math.max(
        (h.columnName ?? "Status").length,
        ...rows.map((r: any) => String(r.disputeStatus ?? "").length),
      );

      return {
        field: h.field,
        headerName: h.columnName,
        flex: 0,
        minWidth: 180,
        width: Math.max(longest * 9, 180),
        renderCell: (p: GridRenderCellParams<T>) => {
          const label = typeof p.value === "string" && p.value ? p.value : "-";

          return (
            <Box sx={{ fontWeight: theme.typography.fontWeightMedium }}>
              {label === "-" ? (
                label
              ) : (
                <Badge
                  type={p.row.badgeType}
                  label={(p.value as string) ?? ""}
                  testId={`badge-${(p.row as any).id}-disputeStatus`}
                />
              )}
            </Box>
          );
        },
        cellClassName: `freeze-right ${shouldFreezeExtraColumn ? "freeze-first" : ""}`,
        headerClassName: `freeze-right ${shouldFreezeExtraColumn ? "freeze-first" : ""}`,
      };
    }

    if (h.field === "departureStatus") {
      const longest = Math.max(
        (h.columnName ?? "Departure Status").length,
        ...rows.map((r: any) => String(r.departureStatus ?? "").length),
      );

      return {
        field: h.field,
        headerName: h.columnName,
        flex: 0,
        minWidth: 180,
        width: Math.max(longest * 9, 180),
        renderCell: (p: GridRenderCellParams<T>) => {
          const label = typeof p.value === "string" && p.value ? p.value : "-";

          return (
            <Box sx={{ fontWeight: theme.typography.fontWeightMedium }}>
              {label === "-" ? (
                label
              ) : (
                <Badge
                  type={
                    p.row.departureStatusBadgeType ||
                    getDepartureStatusBadgeType(p.value as string | undefined)
                  }
                  label={(p.value as string) ?? ""}
                  testId={`badge-${(p.row as any).id}-departureStatus`}
                />
              )}
            </Box>
          );
        },
      };
    }

    if ((h.type as string) === "logsStatus") {
      const percent = (h as any).percent as number | undefined;
      const longest = Math.max(
        (h.columnName ?? "Status").length,
        ...rows.map((r: any) => String(r.status ?? "").length),
      );
      const defaultSizing = { flex: 0, minWidth: 160, width: longest * 9 };
      const percentSizing =
        typeof percent === "number" ? { flex: percent, minWidth: 140 } : defaultSizing;

      return {
        field: h.field,
        headerName: h.columnName,
        ...percentSizing,
        renderCell: (p: GridRenderCellParams<T>) => {
          return (
            <Box sx={{ fontWeight: theme.typography.fontWeightMedium }}>
              <Badge
                type={p.row.badgeType}
                label={(p.value as string) ?? ""}
                testId={`badge-${(p.row as any).id}`}
              />
            </Box>
          );
        },
      };
    }

    if (h.field === "restrictions") {
      const longest = Math.max(
        (h.columnName ?? "Restrictions").length,
        ...rows.map((r: any) => String(r.restrictions ?? "").length),
      );
      const getRestrictionBadgeType = (restriction?: string): "success" | "warning" | "error" =>
        restriction === "Clear to Operate" || restriction === "Released"
          ? "success"
          : restriction === "Interrupted - in Port" || restriction === "Interrupted - leave Port"
            ? "error"
            : "warning";

      return {
        field: h.field,
        headerName: h.columnName,
        flex: 0,
        minWidth: 160,
        width: longest * 9,
        renderCell: (p: GridRenderCellParams<T>) => (
          <Box sx={{ fontWeight: theme.typography.fontWeightMedium }}>
            <Badge
              type={getRestrictionBadgeType(p.value)}
              label={p.value as string}
              testId={`badge-${p.row.id}`}
            />
          </Box>
        ),
        cellClassName: "freeze-right",
        headerClassName: "freeze-right",
      };
    }

    const sizing = sizingFromHeader(h, 1, Math.max(130, h.columnName.length * 5));
    return {
      field: h.field,
      headerName: h.columnName,
      renderHeader: () => (
        <Box
          display="flex"
          alignItems="center"
          gap={0.5}
          dir="ltr"
          sx={{ whiteSpace: "normal", wordBreak: "break-word" }}
        >
          {h.columnName}
          {idx === 0 && (
            <ArrowDownwardIcon sx={{ color: theme.palette.primary.main, pl: 1, display: "none" }} />
          )}
        </Box>
      ),
      ...sizing,
      renderCell: (p: GridRenderCellParams<T>) => (
        <Box
          dir="ltr"
          sx={{
            fontWeight: theme.typography.fontWeightMedium,
            color:
              idx === 0
                ? ((theme.palette.text as any).brand ?? theme.palette.text.primary)
                : theme.palette.text.primary,
          }}
        >
          {p.value || "-"}
        </Box>
      ),
    };
  });
}
